import React from 'react';
import {
  ShieldExclamationIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

/**
 * SummaryView - Dashboard-style summary cards and bar charts.
 *
 * Props:
 * @param {object} summary - Summary data object from results
 *
 * Expected summary shape:
 * {
 *   TotalEvents: 150,
 *   SuccessCount: 130,
 *   FailureCount: 20,
 *   HighRiskCount: 5,
 *   ByApplication: { "Microsoft Teams": 40, "Outlook": 60, "SharePoint": 50 },
 *   ByOS: { "Windows 11": 100, "iOS": 30, "Android": 20 },
 *   ByCountry: { "US": 120, "RU": 15, "CN": 10, "GB": 5 },
 *   ByCity: { "New York": 80, "Moscow": 15, "Beijing": 10 },
 *   ByHour: { "0": 2, "1": 1, "8": 20, "9": 25, "10": 18, "14": 15 }
 * }
 */

export default function SummaryView({ summary = {} }) {
  const {
    TotalEvents = 0,
    HighRiskCount = 0,
    ByApplication = {},
    ByOS = {},
    ByCountry = {},
    ByCity = {},
    ByHour = {},
    ByStatus = {},
  } = summary;
  const SuccessCount = summary.SuccessCount ?? ByStatus.Success ?? 0;
  const FailureCount = summary.FailureCount ?? ByStatus.Failure ?? 0;

  const isEmpty =
    TotalEvents === 0 && Object.keys(ByApplication).length === 0;

  if (isEmpty) {
    return (
      <div className="card text-center py-12">
        <p className="text-gray-500">No summary data available.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Total Events"
          value={TotalEvents}
          icon={ChartBarIcon}
          color="blue"
        />
        <StatCard
          label="Successful"
          value={SuccessCount}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          label="Failed"
          value={FailureCount}
          icon={XCircleIcon}
          color="amber"
        />
        <StatCard
          label="High Risk"
          value={HighRiskCount}
          icon={ShieldExclamationIcon}
          color="red"
        />
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logins by Application */}
        <HorizontalBarChart
          title="Logins by Application"
          icon={ComputerDesktopIcon}
          data={ByApplication}
          barColor="bg-blue-500"
          maxItems={8}
        />

        {/* Logins by OS */}
        <HorizontalBarChart
          title="Logins by OS"
          icon={ComputerDesktopIcon}
          data={ByOS}
          barColor="bg-purple-500"
          maxItems={8}
        />

        {/* Logins by Country */}
        <HorizontalBarChart
          title="Logins by Country"
          icon={GlobeAltIcon}
          data={ByCountry}
          barColor="bg-emerald-500"
          maxItems={10}
        />

        {/* Logins by City */}
        <HorizontalBarChart
          title="Logins by City"
          icon={GlobeAltIcon}
          data={ByCity}
          barColor="bg-cyan-500"
          maxItems={10}
        />
      </div>

      {/* Logins by Hour (Histogram) */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <ClockIcon className="h-5 w-5 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-300">
            Logins by Hour (UTC)
          </h3>
        </div>
        <HourHistogram data={ByHour} />
      </div>

      {/* Success vs Failure Breakdown */}
      <div className="card">
        <h3 className="text-sm font-semibold text-gray-300 mb-4">
          Success vs Failure
        </h3>
        <SuccessFailureBar success={SuccessCount} failure={FailureCount} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StatCard({ label, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-900/30 border-blue-700/30 text-blue-400',
    green: 'bg-green-900/30 border-green-700/30 text-green-400',
    amber: 'bg-amber-900/30 border-amber-700/30 text-amber-400',
    red: 'bg-red-900/30 border-red-700/30 text-red-400',
  };

  const iconColorClasses = {
    blue: 'text-blue-500',
    green: 'text-green-500',
    amber: 'text-amber-500',
    red: 'text-red-500',
  };

  return (
    <div
      className={`rounded-xl border p-4 ${
        colorClasses[color] || colorClasses.blue
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wider">
          {label}
        </span>
        <Icon
          className={`h-5 w-5 ${iconColorClasses[color] || 'text-gray-500'}`}
        />
      </div>
      <p className="text-3xl font-bold text-gray-100">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
    </div>
  );
}

function HorizontalBarChart({ title, icon: Icon, data, barColor, maxItems = 8 }) {
  const entries = Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .slice(0, maxItems);

  if (entries.length === 0) {
    return (
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          {Icon && <Icon className="h-5 w-5 text-gray-500" />}
          <h3 className="text-sm font-semibold text-gray-300">{title}</h3>
        </div>
        <p className="text-sm text-gray-600">No data available.</p>
      </div>
    );
  }

  const maxValue = Math.max(...entries.map(([, v]) => v), 1);

  return (
    <div className="card">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-5 w-5 text-gray-500" />}
        <h3 className="text-sm font-semibold text-gray-300">{title}</h3>
      </div>
      <div className="space-y-2.5">
        {entries.map(([label, count]) => (
          <div key={label}>
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-gray-400 truncate max-w-[180px]" title={label}>
                {label}
              </span>
              <span className="text-gray-500 ml-2 flex-shrink-0">
                {count.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-gray-800 rounded-full h-2">
              <div
                className={`${barColor} h-2 rounded-full transition-all duration-500`}
                style={{ width: `${(count / maxValue) * 100}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HourHistogram({ data }) {
  // Build full 24-hour array
  const hours = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    count: Number(data[String(i)]) || 0,
  }));

  const maxCount = Math.max(...hours.map((h) => h.count), 1);

  return (
    <div className="flex items-end gap-1 h-32">
      {hours.map(({ hour, count }) => {
        const heightPercent = (count / maxCount) * 100;
        const isActive = count > 0;
        return (
          <div
            key={hour}
            className="flex-1 flex flex-col items-center justify-end h-full group relative"
          >
            {/* Tooltip */}
            {isActive && (
              <div className="hidden group-hover:block absolute bottom-full mb-1 px-2 py-1 bg-gray-800 border border-gray-700 rounded text-xs text-gray-200 whitespace-nowrap z-10 shadow-lg">
                {hour.toString().padStart(2, '0')}:00 — {count} event
                {count !== 1 ? 's' : ''}
              </div>
            )}
            {/* Bar */}
            <div
              className={`w-full rounded-t transition-all duration-300 ${
                isActive
                  ? 'bg-blue-500 hover:bg-blue-400'
                  : 'bg-gray-800'
              }`}
              style={{
                height: `${Math.max(heightPercent, isActive ? 4 : 2)}%`,
              }}
            />
            {/* Hour label */}
            <span className="text-[9px] text-gray-600 mt-1 leading-none">
              {hour % 3 === 0 ? `${hour.toString().padStart(2, '0')}` : ''}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function SuccessFailureBar({ success, failure }) {
  const total = success + failure;
  if (total === 0) {
    return <p className="text-sm text-gray-600">No data available.</p>;
  }

  const successPct = ((success / total) * 100).toFixed(1);
  const failurePct = ((failure / total) * 100).toFixed(1);

  return (
    <div>
      <div className="flex h-6 rounded-lg overflow-hidden bg-gray-800">
        {success > 0 && (
          <div
            className="bg-green-500 flex items-center justify-center transition-all duration-500"
            style={{ width: `${successPct}%` }}
          >
            {Number(successPct) > 10 && (
              <span className="text-xs font-medium text-white">
                {successPct}%
              </span>
            )}
          </div>
        )}
        {failure > 0 && (
          <div
            className="bg-red-500 flex items-center justify-center transition-all duration-500"
            style={{ width: `${failurePct}%` }}
          >
            {Number(failurePct) > 10 && (
              <span className="text-xs font-medium text-white">
                {failurePct}%
              </span>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center justify-between mt-2 text-xs">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-green-500 inline-block" />
          <span className="text-gray-400">
            Success: {success.toLocaleString()} ({successPct}%)
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" />
          <span className="text-gray-400">
            Failure: {failure.toLocaleString()} ({failurePct}%)
          </span>
        </div>
      </div>
    </div>
  );
}
